import React, { useEffect, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import {
  curProductAtom,
  qrCodeUrlAtom,
  loadingAtom,
  seatsAtom,
  productsAtom,
  teamInfoAtom,
  visibleResultAtom,
} from '@/store';
import {
  initFn,
  handleRefreshQrCode,
  clearPollTimer,
  initActiveProductIndex,
} from '@/utils/subscribe';
import { getDisplayPrice, getPayChannels } from '@/utils/vip';
import WeChartIcon from '@/assets/image/vip/weChat.png';
import AlipayIcon from '@/assets/image/vip/alipay.png';
import styles from './index.module.css';
import { getUrlParam, showToast } from '@/utils';
import { channelsMap, isTeamSubscription } from '@/constants';
import PayResult from '../new/components/pay-result';
import BindPhone from '../new/components/bind-phone';
import { SubscriptionPeriod } from '@meitu/subscribe-core';

/**
 * 仅有扫描支付界面（目前仅用于团队版场景）
 */
const ScanUI: React.FC = () => {
  // State management using existing atoms
  const [products] = useAtom(productsAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [qrCodeUrl] = useAtom(qrCodeUrlAtom);
  const [loading] = useAtom(loadingAtom);
  const [seats] = useAtom(seatsAtom);
  const [teamInfo] = useAtom(teamInfoAtom);
  const [visibleResult] = useAtom(visibleResultAtom);

  const moduleRef = useRef<HTMLDivElement>(null);
  const hasCheckDefaultSeats = useRef<boolean>(false);

  const userCount = teamInfo?.member_detail?.user_count || 0;

  useEffect(() => {
    initFn();

    return () => {
      clearPollTimer();
    };
  }, []);

  useEffect(() => {
    if (curProduct) {
      handleRefreshQrCode();
    }
  }, [curProduct, seats]);

  useEffect(() => {
    if (products.length) {
      // 初始化商品默认选中状态
      initActiveProductIndex();
    }
  }, [products]);

  useEffect(() => {
    const defaultSeats = +getUrlParam('seats');

    if (!teamInfo || !defaultSeats || hasCheckDefaultSeats.current) return;

    if (userCount > defaultSeats) {
      const tips = `当前团队已有${userCount}人，至少需要购买${userCount}个席位`;
      showToast(tips, moduleRef.current);
    }

    hasCheckDefaultSeats.current = true;
  }, [teamInfo]);

  const { unitPrice, totalPrice, duration, payChannels, payChannelsText } = useMemo(() => {
    // xx月
    let durationValue = 0;
    const subPeriodDuration = curProduct?.subPeriodDuration || 0;
    switch (curProduct?.subPeriod) {
      case SubscriptionPeriod.Day:
        // 天
        durationValue = Math.ceil(subPeriodDuration / 30);
        break;
      case SubscriptionPeriod.PerMonth:
        // 月
        durationValue = subPeriodDuration * 1;
        break;
      case SubscriptionPeriod.PerQuarter:
        // 季
        durationValue = subPeriodDuration * 3;
        break;
      case SubscriptionPeriod.PerYear:
        // 年
        durationValue = subPeriodDuration * 12;
        break;
      default:
        break;
    }
    const unitPriceValue = +((getDisplayPrice(curProduct).payPrice || 0) / durationValue).toFixed(
      2
    );
    const totalPriceValue = isTeamSubscription
      ? +(getDisplayPrice(curProduct).payPrice * seats).toFixed(2)
      : getDisplayPrice(curProduct).payPrice;
    const payChannelsValue: string[] = getPayChannels(curProduct) || [];
    const payChannelsTextValue = payChannelsValue
      .map((item: string) => channelsMap[item] || item)
      .join('/');

    return {
      unitPrice: unitPriceValue,
      totalPrice: totalPriceValue,
      duration: durationValue,
      payChannels: payChannelsValue,
      payChannelsText: payChannelsTextValue,
    };
  }, [curProduct, seats]);

  return (
    <>
      <div className={styles.container} ref={moduleRef}>
        <div className={styles.priceSection}>
          <div className={styles.totalPriceLine}>
            支付<span className={styles.totalPriceAmount}>{totalPrice}</span>元
          </div>
          {isTeamSubscription ? (
            <div className={styles.priceFormula}>
              ¥{unitPrice} × {seats}席位 × {duration}个月
            </div>
          ) : null}
        </div>

        <div className={styles.qrCodeSection}>
          {loading || !qrCodeUrl ? (
            <div className={styles.qrCodePlaceholder}>
              <div className={styles.loadingText}>加载中...</div>
            </div>
          ) : (
            <img src={qrCodeUrl} alt="Payment QR Code" className={styles.qrCode} />
          )}
        </div>

        <div className={styles.paymentMethods}>
          {payChannels.map((item) => {
            if (item === 'weixin') {
              return <img src={WeChartIcon} alt="WeChat Pay" className={styles.payIcon} />;
            } else if (item === 'alipay') {
              return <img src={AlipayIcon} alt="Alipay" className={styles.payIcon} />;
            } else {
              return null;
            }
          })}

          <span className={styles.payText}>{payChannelsText}扫码支付</span>
        </div>

        <div className={styles.agreementText}>
          支付即视为您已同意
          <a
            href="https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html"
            target="_blank"
          >
            《美图设计室会员服务协议》
          </a>
          {isTeamSubscription ? (
            <a
              href="https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html"
              target="_blank"
            >
              《商用授权书》
            </a>
          ) : null}
        </div>
      </div>
      {visibleResult ? <PayResult /> : null}
      <BindPhone />
    </>
  );
};

export default ScanUI;
